import React, { useState, useMemo } from 'react';
import { Plus, Trash2, Eye, EyeOff, Layers, X } from 'lucide-react';

const CORE_ORDER = ['B', 'D', 'Zh', 'Z', 'I', 'L', 'G', 'M', 'A', 'K', 'V', 'E', 'HRT', 'Man', 'SUN'];
const DEFAULT_LABELS = {
  B: 'Б', D: 'Д', Zh: 'Ж', Z: 'З', I: 'И', L: 'Л',
  G: 'Г', M: 'М', A: 'А', K: 'К', V: 'В', E: 'Е',
  HRT: 'HRT', Man: 'Man', SUN: '☀',
};

const ConnectionGroupsEditor = ({ groups = [], nodeLabels = {}, onChange }) => {
  const [newName, setNewName] = useState('');
  const [activeGroupId, setActiveGroupId] = useState(groups[0]?.id || null);
  const [fromNode, setFromNode] = useState('');
  const [toNode, setToNode] = useState('');

  const nodeOptions = useMemo(() => {
    const merged = { ...DEFAULT_LABELS };
    for (const [k, v] of Object.entries(nodeLabels || {})) {
      merged[k] = v.letter || k;
    }
    const fromLabels = Object.keys(merged);
    const ordered = [
      ...CORE_ORDER.filter(id => id in merged),
      ...fromLabels.filter(id => !CORE_ORDER.includes(id)),
    ];
    return ordered.map(id => ({ id, label: typeof merged[id] === 'string' ? merged[id] : (merged[id]?.letter || id) }));
  }, [nodeLabels]);

  const getNodeLabel = (id) => nodeOptions.find(n => n.id === id)?.label || id;

  const activeGroup = groups.find(g => g.id === activeGroupId) || groups[0];

  const updateGroup = (id, changes) => {
    onChange(groups.map(g => g.id === id ? { ...g, ...changes } : g));
  };

  const handleAddGroup = (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    const newId = name.toLowerCase().replace(/[^a-z0-9]/g, '_').slice(0, 30) + '_' + Math.random().toString(36).slice(2, 6);
    // Auto-pick a color from a palette
    const palette = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#ec4899', '#06b6d4', '#84cc16'];
    const color = palette[groups.length % palette.length];
    const newGroup = { id: newId, name, enabled: true, connections: [], color };
    onChange([...groups, newGroup]);
    setActiveGroupId(newId);
    setNewName('');
  };

  const handleDeleteGroup = (id) => {
    if (!window.confirm('Bu qrupu silmək istəyirsiniz?')) return;
    const next = groups.filter(g => g.id !== id);
    onChange(next);
    if (activeGroupId === id) setActiveGroupId(next[0]?.id || null);
  };

  const handleAddConnection = () => {
    if (!activeGroup || !fromNode || !toNode || fromNode === toNode) return;
    const exists = activeGroup.connections.some(
      c => (c[0] === fromNode && c[1] === toNode) || (c[0] === toNode && c[1] === fromNode)
    );
    if (exists) return;
    updateGroup(activeGroup.id, { connections: [...activeGroup.connections, [fromNode, toNode]] });
    setFromNode(''); setToNode('');
  };

  const handleRemoveConnection = (idx) => {
    if (!activeGroup) return;
    updateGroup(activeGroup.id, {
      connections: activeGroup.connections.filter((_, i) => i !== idx),
    });
  };

  return (
    <div className="admin-section" data-testid="connection-groups-section">
      <h3 className="admin-section-title flex items-center gap-2">
        <Layers size={18} className="text-purple-400" /> Bağlantı qrupları
      </h3>
      <p className="admin-section-desc">
        Bağlantıları qruplara ayırın. Hər qrup main səhifədə toggle düyməsi ilə açılıb-bağlana bilər.
        Yalnız aktiv qrupların bağlantıları görünür.
      </p>

      {/* Group list (chips) */}
      <div className="group-chips" data-testid="group-chips">
        {groups.map(g => (
          <button
            key={g.id}
            onClick={() => setActiveGroupId(g.id)}
            className={`group-chip ${activeGroupId === g.id ? 'group-chip-active' : ''}`}
            data-testid={`group-chip-${g.id}`}
            style={activeGroupId === g.id ? { borderColor: g.color || '#a855f7', boxShadow: `0 0 12px ${g.color || '#a855f7'}33` } : undefined}
          >
            <span
              className="group-chip-color-dot"
              style={{ background: g.color || '#a855f7' }}
              title="Qrup rəngi"
            />
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); updateGroup(g.id, { enabled: !g.enabled }); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); updateGroup(g.id, { enabled: !g.enabled }); } }}
              className="group-chip-toggle"
              title={g.enabled ? 'Aktiv' : 'Deaktiv'}
              data-testid={`group-toggle-${g.id}`}
            >
              {g.enabled ? <Eye size={12} /> : <EyeOff size={12} />}
            </span>
            {g.name}
            <span className="group-chip-count">{g.connections.length}</span>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); handleDeleteGroup(g.id); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleDeleteGroup(g.id); } }}
              className="group-chip-delete"
              title="Sil"
              data-testid={`group-delete-${g.id}`}
            >
              <Trash2 size={11} />
            </span>
          </button>
        ))}
      </div>

      {/* Add new group */}
      <form onSubmit={handleAddGroup} className="group-add-form" data-testid="group-add-form">
        <input
          type="text"
          className="karma-input text-left flex-1"
          placeholder="Yeni qrup adı (məs: Çakra zənciri)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          maxLength={50}
          data-testid="new-group-name"
        />
        <button type="submit" disabled={!newName.trim()} className="admin-btn-primary" data-testid="create-group-btn">
          <Plus size={14} /> Qrup
        </button>
      </form>

      {/* Group details */}
      {activeGroup ? (
        <div className="group-details" data-testid="group-details">
          <div className="group-details-header">
            <input
              type="color"
              className="group-color-picker"
              value={activeGroup.color || '#a855f7'}
              onChange={(e) => updateGroup(activeGroup.id, { color: e.target.value })}
              title="Qrup rəngi (bağlantılarda istifadə olunur)"
              data-testid="active-group-color"
            />
            <input
              type="text"
              className="group-name-input"
              value={activeGroup.name}
              onChange={(e) => updateGroup(activeGroup.id, { name: e.target.value })}
              maxLength={50}
              data-testid="active-group-name-input"
            />
            <label className="group-enable-label">
              <input
                type="checkbox"
                checked={activeGroup.enabled}
                onChange={(e) => updateGroup(activeGroup.id, { enabled: e.target.checked })}
                data-testid="active-group-enable"
              />
              <span>Aktiv (footer-də göstər)</span>
            </label>
          </div>

          <div className="conn-add-row">
            <select className="conn-select" value={fromNode} onChange={(e) => setFromNode(e.target.value)}>
              <option value="">Seçin...</option>
              {nodeOptions.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
            </select>
            <span className="conn-arrow">↔</span>
            <select className="conn-select" value={toNode} onChange={(e) => setToNode(e.target.value)}>
              <option value="">Seçin...</option>
              {nodeOptions.filter(n => n.id !== fromNode).map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
            </select>
            <button onClick={handleAddConnection} disabled={!fromNode || !toNode || fromNode === toNode} className="conn-add-btn">
              <Plus size={16} /> Əlavə
            </button>
          </div>

          <div className="conn-list">
            <div className="conn-count">Qrupda: {activeGroup.connections.length} bağlantı</div>
            {activeGroup.connections.map((conn, i) => (
              <div key={`${conn[0]}-${conn[1]}-${i}`} className="conn-item">
                <span className="conn-node">{getNodeLabel(conn[0])}</span>
                <span className="conn-line-icon">—</span>
                <span className="conn-node">{getNodeLabel(conn[1])}</span>
                <button onClick={() => handleRemoveConnection(i)} className="conn-remove" title="Sil">
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-white/40 text-sm">Hələ qrup yoxdur. Yuxarıdakı sahədə yeni qrup yaradın.</p>
      )}
    </div>
  );
};

export default ConnectionGroupsEditor;
